import { DatePipe } from '@angular/common';
import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, ConfirmationComponent, Integration, IntegrationService, Pager, IntegrationTypeEnum } from 'lib-trend-core';
import { debounceTime, distinctUntilChanged, map, Observable, Subject, switchMap, takeUntil } from 'rxjs';

@Component({
    selector: 'pipedrive-integrations-list-component',
    templateUrl: 'pipedrive-integration-list.v2.component.html',
    // templateUrl: 'pipedrive-integration-list.component.html',
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PipedriveListComponent extends AbstractComponent implements OnInit {

  pager: Pager<Integration> = new Pager<Integration>({ perPage: 10 });
  listObservable: Observable<Pager<Integration>>;
  private termOfSearch: Subject<string> = new Subject<string>();

  processedList: Integration[] = [];
  breadcrumb = [
    { href: '/integrations', label: 'integrations.breadcrumb.integrations' },
    { label: 'integrations.breadcrumb.pipedrive' },
  ]


  constructor(
    public injector: Injector,
    public dialog: MatDialog,
    private integrationService: IntegrationService
  ) {
    super(injector);
  }

  ngOnInit() {
    this.getList();
  }

  getList() {
    this.loadingContent = true;
    this.searchParams = {
      company: this.getIDCurrentCompany(),
      type: IntegrationTypeEnum.PIPEDRIVE
    };
    this.integrationService.getAll(this.pager.page, this.pager.perPage, this.searchString, {
      company: this.getIDCurrentCompany()
    })
      .pipe(
        takeUntil(this.destroy$),
        map((pager: Pager<Integration>) => {
          const pipedriveIntegrations = pager.list.filter(
            integration => integration.type === IntegrationTypeEnum.PIPEDRIVE
          );

          return {
            ...pager,
            list: pipedriveIntegrations,
            total: pipedriveIntegrations.length
          };
        })
      )
      .subscribe({
        next: (pager: Pager<Integration>) => {
          this.pager = pager;
          this.processedList = pager.list;
          this.setupObservableSearch();
        },
        error: (err) => {
          this.loadingContent = false;
        },
        complete: () => (this.loadingContent = false),
      });
  }

  setupObservableSearch() {
    this.listObservable = this.termOfSearch.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((term) => {
        return this.integrationService.getAll(
          this.pager.page,
          this.pager.perPage,
          term,
          this.searchParams
        )
      })
    )

    this.listObservable.subscribe({
      next: (pager: Pager<Integration>) => {
        this.pager = pager
        this.processedList = pager.list.filter((item) =>
          item.type === IntegrationTypeEnum.PIPEDRIVE &&
          item.name.toLowerCase().includes(this.searchString.toLowerCase())
        )
      },
      error: (err) => {
        console.error('Error in listObservable:', err)
      },
    })
  }

  removeIntegration(integration: Integration) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '700px',
      data: this.t('integrations.pipedriveList.removeConfirm', { name: integration.name })
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadingContent = true;
        this.integrationService.delete(integration._id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.alertService.success(this.t('integrations.pipedriveList.removeSuccess'));
              this.getList();
            },
            error: (error) => {
              const errorMessage = error?.error?.message || this.t('integrations.pipedriveList.removeError');
              this.alertService.error(errorMessage);
              this.loadingContent = false;
            }
          });
      }
    });
  }

  search(term: string) {
    this.pager.page = 1;
    this.searchString = term;
    this.termOfSearch.next(term);
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getList();
  }

  editIntegration(integration: Integration) {
    this.router.navigate([`/integrations/pipedrive/${integration._id}/edit`]);
  }

  goLeadRules(integration: Integration) {
    this.router.navigate([`/integrations/pipedrive/${integration._id}/rules`]);
  }
}
